/**
 * A galeria de modelos: quais aparecem para quem, e a configuração de cada um.
 *
 * ── O que a galeria mostra ────────────────────────────────────────────────
 *
 * Só os modelos que a pessoa pode montar e que o serviço já sabe rodar: a
 * empresa monta os internos, a Cliqfy os que leem o banco do cliente (a mesma
 * régua de `criarDoModelo` no backend), e o que vem com `disponivel: false` não
 * aparece. Também só os que têm formulário aqui: um modelo novo do serviço
 * entra na galeria junto com o formulário dele.
 *
 * ── A configuração ────────────────────────────────────────────────────────
 *
 * O formulário guarda texto (o que a pessoa digita); daqui sai a configuração
 * no formato de `CONFIGURACOES_DOS_MODELOS`, e os problemas, cada um pelo
 * campo, antes de ir ao serviço. O que só o serviço confere (a operação ser
 * desta organização, as expressões das ações) volta como erro da API.
 */

import type { CriarAutomacao, ModelosDeAutomacao } from '@cliqcrm/contracts';
import { MONTA_AUTOMACAO_COM_SQL } from '@/entities/acesso/papel';
import type { NomeDoIcone } from '@/shared/ui';

export type Modelo = ModelosDeAutomacao['itens'][number];

export const MODELO_REDISTRIBUIR = 'redistribute-on-no-first-reply';
export const MODELO_LEMBRETE = 'appointment-reminder-and-confirmation';
export const MODELO_ROTINA = 'scheduled-records-to-crm';
export const MODELO_MENU = 'channel-menu-with-inactivity-close';
export const MODELO_RESPONSAVEL = 'opportunity-owner-from-conversation-taker';

const COM_FORMULARIO = new Set([
  MODELO_REDISTRIBUIR,
  MODELO_LEMBRETE,
  MODELO_ROTINA,
  MODELO_MENU,
  MODELO_RESPONSAVEL,
]);

/**
 * A arte do cartão na galeria: a categoria e os três passos do modelo.
 *
 * O catálogo do serviço traz nome e descrição, não os passos. Como o ícone
 * (`ICONE_DO_MODELO`), a arte é desenho de um modelo que o front já conhece
 * (é ele que tem o formulário de cada um), e descreve o que o formulário
 * configura. Um modelo novo do serviço entra sem arte: o cartão mostra o
 * ícone, e não passos inventados.
 */
export type ArteDoModelo = {
  categoria: string;
  passos: readonly [
    { rotulo: string; icone: NomeDoIcone },
    { rotulo: string; icone: NomeDoIcone },
    { rotulo: string; icone: NomeDoIcone },
  ];
};

const ARTE_DO_MODELO: Record<string, ArteDoModelo> = {
  [MODELO_MENU]: {
    categoria: 'Atendimento',
    passos: [
      { rotulo: 'Chega mensagem', icone: 'atendimento' },
      { rotulo: 'Envia o menu', icone: 'menu' },
      { rotulo: 'Leva ao destino', icone: 'check' },
    ],
  },
  [MODELO_REDISTRIBUIR]: {
    categoria: 'Atendimento',
    passos: [
      { rotulo: 'Conversa chega', icone: 'atendimento' },
      { rotulo: 'Ninguém responde', icone: 'relogio' },
      { rotulo: 'Passa adiante', icone: 'passar' },
    ],
  },
  [MODELO_RESPONSAVEL]: {
    categoria: 'Vendas',
    passos: [
      { rotulo: 'Alguém assume', icone: 'pessoa' },
      { rotulo: 'Acha a oportunidade', icone: 'funil' },
      { rotulo: 'Vira o responsável', icone: 'check' },
    ],
  },
  [MODELO_LEMBRETE]: {
    categoria: 'Agenda',
    passos: [
      { rotulo: 'No horário', icone: 'calendario' },
      { rotulo: 'Lê as consultas', icone: 'documento' },
      { rotulo: 'Lembra e confirma', icone: 'check' },
    ],
  },
  [MODELO_ROTINA]: {
    categoria: 'Rotinas',
    passos: [
      { rotulo: 'No horário', icone: 'relogio' },
      { rotulo: 'Lê a lista', icone: 'documento' },
      { rotulo: 'Grava no CRM', icone: 'check' },
    ],
  },
};

export function arteDoModelo(chave: string): ArteDoModelo | null {
  return ARTE_DO_MODELO[chave] ?? null;
}

export function modelosOferecidos(modelos: readonly Modelo[], papel: string | null): Modelo[] {
  if (!papel) return [];
  const comSql = MONTA_AUTOMACAO_COM_SQL.has(papel);
  return modelos.filter((m) => m.disponivel && m.usa_sql === comSql && COM_FORMULARIO.has(m.chave));
}

// ── Horas e dias ─────────────────────────────────────────────────────────

export const DIAS_DA_SEMANA = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
] as const;

export const FUSOS: readonly { valor: string; rotulo: string }[] = [
  { valor: 'America/Sao_Paulo', rotulo: 'Horário de Brasília' },
  { valor: 'America/Manaus', rotulo: 'Manaus (uma hora a menos)' },
  { valor: 'America/Cuiaba', rotulo: 'Cuiabá (uma hora a menos)' },
  { valor: 'America/Rio_Branco', rotulo: 'Rio Branco (duas horas a menos)' },
  { valor: 'America/Noronha', rotulo: 'Fernando de Noronha (uma hora a mais)' },
];

/** "08:30" → 510; o que não é hora vira `null`. */
export function minutosDoDia(hora: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hora.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/**
 * O fim do expediente: "00:00" (o que o campo de hora deixa escrever) e
 * "24:00" são a meia-noite do fim do dia.
 */
function minutosDoFim(hora: string): number | null {
  const h = hora.trim();
  return h === '24:00' || h === '00:00' ? 1440 : minutosDoDia(h);
}

// ── O formulário de cada modelo ──────────────────────────────────────────

export type DiaDoExpediente = { ligado: boolean; inicio: string; fim: string };

export type FormularioRedistribuir = {
  minutos: string;
  fuso: string;
  dias: DiaDoExpediente[];
  modeloDaMensagem: string;
};

/** Um horário da agenda: `dia` é "todo" (todo dia) ou o número do dia, "0" a "6". */
export type HorarioDaAgenda = { dia: string; hora: string };

export type FormularioAgenda = { fuso: string; horarios: HorarioDaAgenda[] };

export type FormularioLembrete = {
  agenda: FormularioAgenda;
  operacaoId: string;
  modeloDoLembrete: string;
};

export type FormularioRotina = {
  agenda: FormularioAgenda;
  operacaoId: string;
  /** Os valores que a consulta recebe, na ordem dela: expressões, como as da ação. */
  parametrosDaOperacao: string[];
  acao: string;
  parametros: Record<string, string>;
};

export function formularioRedistribuirInicial(): FormularioRedistribuir {
  return {
    minutos: '15',
    fuso: 'America/Sao_Paulo',
    // Segunda a sexta, das 8 às 18: o expediente mais comum, que a pessoa ajusta.
    dias: DIAS_DA_SEMANA.map((_, i) => ({
      ligado: i >= 1 && i <= 5,
      inicio: '08:00',
      fim: '18:00',
    })),
    modeloDaMensagem: '',
  };
}

export function agendaInicial(): FormularioAgenda {
  return { fuso: 'America/Sao_Paulo', horarios: [{ dia: 'todo', hora: '08:00' }] };
}

/** Os problemas, pelo nome do campo do formulário. Vazio: dá para criar. */
export type Problemas = Record<string, string>;

function problemasDaAgenda(agenda: FormularioAgenda): Problemas {
  const p: Problemas = {};
  if (agenda.horarios.length === 0) p['agenda'] = 'Diga pelo menos um horário em que ela roda.';
  // O mesmo dia e a mesma hora duas vezes: a tela aceitava, e a rotina gravava
  // o horário repetido sem dizer nada (achado do teste do Funil 4, 27/10).
  const vistos = new Set<string>();
  agenda.horarios.forEach((h, i) => {
    const minutos = minutosDoDia(h.hora);
    if (minutos === null) {
      p[`agenda.${i}`] = 'Escreva a hora como 08:00.';
      return;
    }
    // "Todo dia" já inclui cada dia da semana: as 8h de todo dia e as 8h de
    // segunda rodariam duas vezes na segunda.
    const repetido =
      vistos.has(`${h.dia}:${minutos}`) ||
      (h.dia === 'todo'
        ? [...vistos].some((v) => v.endsWith(`:${minutos}`))
        : vistos.has(`todo:${minutos}`));
    if (repetido) p[`agenda.${i}`] = 'Este horário já está na lista.';
    vistos.add(`${h.dia}:${minutos}`);
  });
  return p;
}

function agendaDaConfiguracao(agenda: FormularioAgenda) {
  return {
    fuso: agenda.fuso,
    horarios: agenda.horarios.map((h) => {
      const minutos = minutosDoDia(h.hora) ?? 0;
      return {
        dia_da_semana: h.dia === 'todo' ? null : Number(h.dia),
        hora: Math.floor(minutos / 60),
        minuto: minutos % 60,
      };
    }),
  };
}

export function problemasRedistribuir(f: FormularioRedistribuir): Problemas {
  const p: Problemas = {};
  const minutos = Number(f.minutos);
  if (!Number.isInteger(minutos) || minutos < 1 || minutos > 10_080) {
    p['minutos'] = 'Diga o prazo em minutos inteiros, de 1 a 10.080 (uma semana).';
  }
  if (!f.dias.some((d) => d.ligado)) p['dias'] = 'Marque pelo menos um dia de expediente.';
  f.dias.forEach((d, i) => {
    if (!d.ligado) return;
    const inicio = minutosDoDia(d.inicio);
    const fim = minutosDoFim(d.fim);
    if (inicio === null || fim === null) p[`dias.${i}`] = 'Escreva as horas como 08:00.';
    else if (inicio >= fim) p[`dias.${i}`] = 'O expediente começa antes de terminar.';
  });
  return p;
}

export function configuracaoRedistribuir(f: FormularioRedistribuir): Record<string, unknown> {
  const modelo = f.modeloDaMensagem.trim();
  return {
    minutos_sem_resposta: Number(f.minutos),
    horario_comercial: {
      fuso: f.fuso,
      janelas: f.dias.flatMap((d, i) =>
        d.ligado
          ? [
              {
                dia_da_semana: i,
                inicio_minuto: minutosDoDia(d.inicio) ?? 0,
                fim_minuto: minutosDoFim(d.fim) ?? 0,
              },
            ]
          : [],
      ),
    },
    ...(modelo ? { modelo_de_mensagem_ao_cliente: modelo } : {}),
  };
}

export function problemasLembrete(f: FormularioLembrete): Problemas {
  const p: Problemas = problemasDaAgenda(f.agenda);
  if (!f.operacaoId) p['operacao'] = 'Escolha a operação que traz os compromissos.';
  if (!f.modeloDoLembrete.trim()) {
    p['modeloDoLembrete'] = 'Diga o nome do modelo de mensagem aprovado no WhatsApp.';
  }
  return p;
}

export function configuracaoLembrete(f: FormularioLembrete): Record<string, unknown> {
  return {
    agenda: agendaDaConfiguracao(f.agenda),
    operacao_id: f.operacaoId,
    modelo_do_lembrete: f.modeloDoLembrete.trim(),
  };
}

export type ParametroDaAcao = {
  nome: string;
  titulo: string;
  tipo?: string;
  obrigatorio: boolean;
  /** Os valores para escolher, quando o CRM os manda (a etapa, a pessoa). */
  opcoes?: readonly { valor: string; rotulo: string }[];
  /** O nome da escolha que manda `null`, quando a ação a aceita. */
  opcao_nula?: string;
  /** `false`: dado do cliente, que não fica fixo na automação (vem do evento ou da linha). */
  valor_fixo?: boolean;
};

/** O nome com que cada linha da consulta chega à ação: `registro.coluna`. */
export const LINHA_DA_ROTINA = 'registro';

/**
 * O caminho de uma coluna da linha: `registro.paciente`, ou, com espaço, hífen
 * ou acento no nome, entre crases (`` registro.`telefone do paciente` ``), que
 * é como o JSONata lê um nome fora do padrão.
 */
export function caminhoDaColuna(coluna: string): string {
  return /^[A-Za-z_]\w*$/.test(coluna)
    ? `${LINHA_DA_ROTINA}.${coluna}`
    : `${LINHA_DA_ROTINA}.\`${coluna}\``;
}

/**
 * Um texto com `{coluna}` como expressão: "Consulta de {paciente}" vira
 * `"Consulta de " & registro.paciente`. Sem coluna, o texto fixo, entre aspas.
 * Coluna que não está na lista fica como texto, para a pessoa ver o que errou.
 */
export function expressaoDoTexto(texto: string, colunas: readonly string[]): string {
  const partes: string[] = [];
  let resto = texto;
  for (;;) {
    const m = /\{([^{}]+)\}/.exec(resto);
    if (!m) break;
    const antes = resto.slice(0, m.index);
    if (antes) partes.push(JSON.stringify(antes));
    partes.push(colunas.includes(m[1]!) ? caminhoDaColuna(m[1]!) : JSON.stringify(m[0]));
    resto = resto.slice(m.index + m[0].length);
  }
  if (resto || partes.length === 0) partes.push(JSON.stringify(resto));
  return partes.join(' & ');
}

export type ComoAcharOContato = 'telefone' | 'cpf' | 'cnpj';

/** A referência do contato pela coluna da linha: o telefone direto, o documento com o prefixo. */
export function expressaoDoContato(coluna: string, como: ComoAcharOContato): string {
  const valor = caminhoDaColuna(coluna);
  return como === 'telefone' ? valor : `"${como}:" & ${valor}`;
}

/**
 * Como a coluna aponta a oportunidade: o id dela, ou a referência que um
 * "Criar oportunidade" ligou ao card (`ref:` na ação).
 */
export type ComoAcharAOportunidade = 'id' | 'referencia';

/** A oportunidade pela coluna da linha: o id direto, a referência com o prefixo. */
export function expressaoDaOportunidade(coluna: string, como: ComoAcharAOportunidade): string {
  const valor = caminhoDaColuna(coluna);
  return como === 'id' ? valor : `"ref:" & ${valor}`;
}

/** A coluna e o jeito, lidos de volta da expressão; `null` se ela não é de uma coluna. */
export function oportunidadeDaExpressao(
  expressao: string,
  colunas: readonly string[],
): { coluna: string; como: ComoAcharAOportunidade } | null {
  const limpa = expressao.trim();
  for (const coluna of colunas) {
    if (limpa === expressaoDaOportunidade(coluna, 'id')) return { coluna, como: 'id' };
    if (limpa === expressaoDaOportunidade(coluna, 'referencia')) {
      return { coluna, como: 'referencia' };
    }
  }
  return null;
}

/** Os campos da oportunidade que a ação aceita: o nome dela e o do contato novo. */
export function expressaoDosCamposDaOportunidade(
  nome: string,
  nomeDoContato: string,
  colunas: readonly string[],
): string {
  const pares = [
    ...(nome.trim() ? [`"nome": ${expressaoDoTexto(nome.trim(), colunas)}`] : []),
    ...(nomeDoContato.trim()
      ? [`"nome_do_contato": ${expressaoDoTexto(nomeDoContato.trim(), colunas)}`]
      : []),
  ];
  return pares.length > 0 ? `{${pares.join(', ')}}` : '';
}

export function problemasRotina(
  f: FormularioRotina,
  parametrosDaAcao: readonly ParametroDaAcao[],
): Problemas {
  const p: Problemas = problemasDaAgenda(f.agenda);
  if (!f.operacaoId) p['operacao'] = 'Escolha a operação cujas linhas viram ações.';
  f.parametrosDaOperacao.forEach((valor, i) => {
    if (!valor.trim()) p[`operacao.${i}`] = 'Diga o valor que a consulta recebe.';
  });
  if (!f.acao) p['acao'] = 'Escolha o que fazer com cada linha.';
  for (const parametro of parametrosDaAcao) {
    if (parametro.obrigatorio && !(f.parametros[parametro.nome] ?? '').trim()) {
      p[`parametro.${parametro.nome}`] = `Preencha “${parametro.titulo}”.`;
    }
  }
  return p;
}

export function configuracaoRotina(f: FormularioRotina): Record<string, unknown> {
  const parametros = Object.fromEntries(
    Object.entries(f.parametros)
      .map(([nome, valor]) => [nome, valor.trim()] as const)
      .filter(([, valor]) => valor !== ''),
  );
  return {
    agenda: agendaDaConfiguracao(f.agenda),
    operacao_id: f.operacaoId,
    ...(f.parametrosDaOperacao.length > 0
      ? { parametros_da_operacao: f.parametrosDaOperacao.map((v) => v.trim()) }
      : {}),
    linha_em: LINHA_DA_ROTINA,
    acao: { acao: f.acao, parametros },
  };
}

// ── Ler de volta: a configuração salva no formulário (editar a rotina) ──

/** Um pedaço da expressão: texto entre aspas, coluna da linha ou sinal (`&`, `,`, `:`, `{`, `}`). */
type Ficha =
  | { readonly tipo: 'texto'; readonly valor: string }
  | { readonly tipo: 'coluna'; readonly valor: string }
  | { readonly tipo: 'sinal'; readonly valor: string };

const TEXTO_ENTRE_ASPAS = /^"(?:[^"\\]|\\.)*"/;
const TEXTO_ENTRE_ASPAS_SIMPLES = /^'([^'\\]*)'/;
const COLUNA_SIMPLES = new RegExp(`^${LINHA_DA_ROTINA}\\.([A-Za-z_]\\w*)`);
const COLUNA_ENTRE_CRASES = new RegExp(`^${LINHA_DA_ROTINA}\\.\`([^\`]+)\``);

/**
 * Os pedaços de uma expressão que a tela montou (`expressaoDoTexto` e as
 * vizinhas). Qualquer outra coisa (uma função, uma conta) é `null`: a tela
 * mostra a expressão como está, em vez de adivinhar.
 */
function fichas(expressao: string): Ficha[] | null {
  const lista: Ficha[] = [];
  let resto = expressao.trim();
  while (resto) {
    const texto = TEXTO_ENTRE_ASPAS.exec(resto);
    // O texto entre aspas simples, que o JSONata também aceita ('sisac').
    const simples = texto ? null : TEXTO_ENTRE_ASPAS_SIMPLES.exec(resto);
    const coluna = COLUNA_ENTRE_CRASES.exec(resto) ?? COLUNA_SIMPLES.exec(resto);
    if (texto) lista.push({ tipo: 'texto', valor: JSON.parse(texto[0]) as string });
    else if (simples) lista.push({ tipo: 'texto', valor: simples[1]! });
    else if (coluna) lista.push({ tipo: 'coluna', valor: coluna[1]! });
    else if ('&,:{}'.includes(resto[0]!)) lista.push({ tipo: 'sinal', valor: resto[0]! });
    else return null;
    resto = resto.slice((texto ?? simples ?? coluna)?.[0].length ?? 1).trimStart();
  }
  return lista;
}

/** Valor & valor & …: o texto com `{coluna}`, ou `null`. */
function textoDasFichas(lista: readonly Ficha[], colunas: readonly string[]): string | null {
  let texto = '';
  for (const [i, f] of lista.entries()) {
    const esperaValor = i % 2 === 0;
    if (esperaValor === (f.tipo === 'sinal')) return null;
    if (f.tipo === 'sinal' && f.valor !== '&') return null;
    if (f.tipo === 'texto') texto += f.valor;
    // Coluna que a consulta não traz mais: lida, ela viraria texto ao salvar.
    if (f.tipo === 'coluna') {
      if (!colunas.includes(f.valor)) return null;
      texto += `{${f.valor}}`;
    }
  }
  return lista.length % 2 === 1 ? texto : null;
}

/** O texto com `{coluna}` de volta (o inverso de `expressaoDoTexto`), ou `null`. */
export function textoDaExpressao(expressao: string, colunas: readonly string[]): string | null {
  const lista = fichas(expressao);
  return lista ? textoDasFichas(lista, colunas) : null;
}

/** A coluna e o jeito do contato de volta (o inverso de `expressaoDoContato`), ou `null`. */
export function contatoDaExpressao(
  expressao: string,
  colunas: readonly string[],
): { coluna: string; como: ComoAcharOContato } | null {
  const limpa = expressao.trim();
  for (const coluna of colunas) {
    for (const como of ['telefone', 'cpf', 'cnpj'] as const) {
      if (limpa === expressaoDoContato(coluna, como)) return { coluna, como };
    }
  }
  return null;
}

/** O nome do card e o do contato de volta (o inverso de `expressaoDosCamposDaOportunidade`). */
export function camposDaOportunidadeDaExpressao(
  expressao: string,
  colunas: readonly string[],
): { nome: string; nomeDoContato: string } | null {
  const lista = fichas(expressao);
  if (!lista || lista[0]?.valor !== '{' || lista.at(-1)?.valor !== '}') return null;
  const campos = { nome: '', nomeDoContato: '' };
  const chaves: Record<string, keyof typeof campos> = {
    nome: 'nome',
    nome_do_contato: 'nomeDoContato',
  };
  let pares = lista.slice(1, -1);
  while (pares.length > 0) {
    const [chave, doisPontos] = pares;
    const fim = pares.findIndex((f) => f.tipo === 'sinal' && f.valor === ',');
    const valor = pares.slice(2, fim === -1 ? undefined : fim);
    const campo = chave?.tipo === 'texto' ? chaves[chave.valor] : undefined;
    if (!campo || doisPontos?.valor !== ':') return null;
    const texto = textoDasFichas(valor, colunas);
    if (texto === null) return null;
    campos[campo] = texto;
    pares = fim === -1 ? [] : pares.slice(fim + 1);
  }
  return campos;
}

function agendaDoFormulario(agenda: unknown): FormularioAgenda {
  const a = agenda as {
    fuso?: string;
    horarios?: { dia_da_semana: number | null; hora: number; minuto: number }[];
  };
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  return {
    fuso: a.fuso ?? 'America/Sao_Paulo',
    horarios: (a.horarios ?? []).map((h) => ({
      dia: h.dia_da_semana === null ? 'todo' : String(h.dia_da_semana),
      hora: `${doisDigitos(h.hora)}:${doisDigitos(h.minuto)}`,
    })),
  };
}

/** O formulário da rotina com a configuração salva (o inverso de `configuracaoRotina`). */
export function formularioRotinaDe(c: Readonly<Record<string, unknown>>): FormularioRotina {
  const acao = (c['acao'] ?? {}) as { acao?: string; parametros?: Record<string, string> };
  return {
    agenda: agendaDoFormulario(c['agenda']),
    operacaoId: typeof c['operacao_id'] === 'string' ? c['operacao_id'] : '',
    parametrosDaOperacao: [...((c['parametros_da_operacao'] as string[] | undefined) ?? [])],
    acao: acao.acao ?? '',
    parametros: { ...acao.parametros },
  };
}

/** O formulário do lembrete com a configuração salva (o inverso de `configuracaoLembrete`). */
export function formularioLembreteDe(c: Readonly<Record<string, unknown>>): FormularioLembrete {
  return {
    agenda: agendaDoFormulario(c['agenda']),
    operacaoId: typeof c['operacao_id'] === 'string' ? c['operacao_id'] : '',
    modeloDoLembrete: typeof c['modelo_do_lembrete'] === 'string' ? c['modelo_do_lembrete'] : '',
  };
}

/**
 * O que a configuração salva tem e o formulário não mostra (o que a consulta
 * do lembrete recebe, a conversa de cada compromisso): vai junto ao salvar,
 * para editar o horário não apagar o resto.
 */
export function restoDaConfiguracao(
  chave: string,
  c: Readonly<Record<string, unknown>>,
  operacaoId: string,
): Record<string, unknown> {
  // O que a consulta recebe é da consulta salva: trocada a consulta, ele não vai.
  const mesmaConsulta = c['operacao_id'] === operacaoId;
  const fora =
    chave === MODELO_LEMBRETE
      ? ['conversa', ...(mesmaConsulta ? ['parametros_da_operacao'] : [])]
      : [];
  return Object.fromEntries(fora.filter((k) => c[k] !== undefined).map((k) => [k, c[k]]));
}

// ── O menu por canal ─────────────────────────────────────────────────────

/** Uma opção do menu: o nome que a pessoa vê e a ação que ela dispara. */
export type OpcaoDoMenu = {
  rotulo: string;
  acao: string;
  parametros: Record<string, string>;
  /**
   * Só da tela, não vai ao serviço: o que identifica a opção na lista. Tirar a
   * opção 1 não pode fazer a 2 herdar os campos dela.
   */
  chave?: string;
};

export type FormularioMenu = {
  canalId: string;
  textoDoMenu: string;
  opcoes: OpcaoDoMenu[];
  textoDoLembrete: string;
  lembrarAposMinutos: string;
  encerrarAposMinutos: string;
  motivoDoEncerramento: string;
};

/** O contrato aceita até 10 opções, cada uma com até 24 letras no nome. */
export const MAXIMO_DE_OPCOES = 10;
const MAXIMO_DO_ROTULO = 24;

export function opcaoVazia(): OpcaoDoMenu {
  return { rotulo: '', acao: '', parametros: {}, chave: crypto.randomUUID() };
}

export function formularioMenuInicial(): FormularioMenu {
  return {
    canalId: '',
    textoDoMenu: '',
    opcoes: [opcaoVazia()],
    textoDoLembrete: 'Ainda está por aí? Escolha uma das opções para seguirmos.',
    lembrarAposMinutos: '10',
    encerrarAposMinutos: '30',
    motivoDoEncerramento: 'Encerrada por falta de resposta ao menu.',
  };
}

function minutosValidos(texto: string): boolean {
  const n = Number(texto);
  return Number.isInteger(n) && n >= 1 && n <= 10_080;
}

export function problemasMenu(
  f: FormularioMenu,
  parametrosDaAcao: (acao: string) => readonly ParametroDaAcao[],
): Problemas {
  const p: Problemas = {};
  if (!f.canalId) p['canal'] = 'Escolha o canal em que o menu aparece.';
  if (!f.textoDoMenu.trim()) p['textoDoMenu'] = 'Escreva a mensagem que abre o menu.';
  f.opcoes.forEach((o, i) => {
    const rotulo = o.rotulo.trim();
    if (!rotulo) p[`opcao.${i}.rotulo`] = 'Dê um nome à opção.';
    else if (rotulo.length > MAXIMO_DO_ROTULO) {
      p[`opcao.${i}.rotulo`] = `Use até ${MAXIMO_DO_ROTULO} letras: é o texto do botão.`;
    }
    if (!o.acao) p[`opcao.${i}.acao`] = 'Escolha o que acontece nesta opção.';
    for (const parametro of parametrosDaAcao(o.acao)) {
      if (parametro.obrigatorio && !(o.parametros[parametro.nome] ?? '').trim()) {
        p[`opcao.${i}.parametro.${parametro.nome}`] = `Preencha “${parametro.titulo}”.`;
      }
    }
  });
  if (!f.textoDoLembrete.trim()) p['textoDoLembrete'] = 'Escreva o lembrete.';
  if (!minutosValidos(f.lembrarAposMinutos)) {
    p['lembrar'] = 'Diga os minutos inteiros, de 1 a 10.080 (uma semana).';
  }
  if (!minutosValidos(f.encerrarAposMinutos)) {
    p['encerrar'] = 'Diga os minutos inteiros, de 1 a 10.080 (uma semana).';
  }
  if (f.motivoDoEncerramento.trim().length < 3) {
    p['motivo'] = 'Escreva o motivo do encerramento, com pelo menos 3 letras.';
  }
  return p;
}

function parametrosPreenchidos(parametros: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(parametros)
      .map(([nome, valor]) => [nome, valor.trim()] as const)
      .filter(([, valor]) => valor !== ''),
  );
}

export function configuracaoMenu(f: FormularioMenu): Record<string, unknown> {
  return {
    canal_id: f.canalId,
    texto_do_menu: f.textoDoMenu.trim(),
    // O id é a posição: o serviço só pede que não se repita.
    opcoes: f.opcoes.map((o, i) => ({
      id: `opcao-${i + 1}`,
      rotulo: o.rotulo.trim(),
      acoes: [{ acao: o.acao, parametros: parametrosPreenchidos(o.parametros) }],
    })),
    texto_do_lembrete: f.textoDoLembrete.trim(),
    lembrar_apos_minutos: Number(f.lembrarAposMinutos),
    encerrar_apos_minutos: Number(f.encerrarAposMinutos),
    motivo_do_encerramento: f.motivoDoEncerramento.trim(),
  };
}

// ── Começar do zero ──────────────────────────────────────────────────────

type EventoDoGatilho = Extract<CriarAutomacao['gatilho'], { tipo: 'evento' }>['evento'];

/**
 * O início com que nasce quem começa do zero: "Chegou uma mensagem", o mais
 * comum. A pessoa troca no próprio mapa, pela paleta "Começa quando".
 */
export const EVENTO_DE_QUEM_COMECA_DO_ZERO = 'conversa.mensagem_recebida';

/** O id do primeiro bloco do mapa de quem começa do zero. */
export const BLOCO_INICIAL = 'esperar';

/**
 * A automação de quem começa do zero: o evento escolhido e um mapa com um bloco
 * só, uma espera de uma hora, que não faz nada no CRM. O serviço não aceita mapa
 * vazio; a pessoa troca e acrescenta os blocos no editor. Ela nasce rascunho,
 * e não roda até alguém ativar.
 */
export function configuracaoDoZero(evento: string): Omit<CriarAutomacao, 'nome' | 'descricao'> {
  return {
    gatilho: { tipo: 'evento', evento: evento as EventoDoGatilho },
    condicao: null,
    outros_comecos: [],
    grafo: {
      inicio: BLOCO_INICIAL,
      nos: [
        {
          id: BLOCO_INICIAL,
          tipo: 'esperar_tempo',
          milissegundos: 3_600_000,
          posicao: { x: 0, y: 0 },
        },
      ],
      ligacoes: [],
    },
  };
}
